import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Autocomplete input component with dropdown suggestions.
 * Supports keyboard navigation and debounced API calls.
 */
const AutocompleteInput = ({
  value,
  onChange,
  onSelect,
  fetchSuggestions,
  placeholder = 'Search...',
  minChars = 2,
  debounceMs = 300,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Memoize fetchSuggestions to avoid unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFetchSuggestions = useCallback(fetchSuggestions, []);

  useEffect(() => {
    if (value.length < minChars) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    // Debounce API calls
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const results = await stableFetchSuggestions(value);
        setSuggestions(results || []);
        setIsOpen(results && results.length > 0);
        setHighlightedIndex(-1);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Autocomplete error:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(debounceRef.current);
  }, [value, stableFetchSuggestions, minChars, debounceMs]);

  const handleSelect = (item) => {
    onSelect(item);
    setIsOpen(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleBlur = () => {
    // Delay closing to allow click on suggestion
    setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="input-dark w-full"
        autoComplete="off"
      />

      {/* Loading spinner */}
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      )}

      {/* Suggestions dropdown */}
      {isOpen && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((item, index) => (
            <li
              key={item.email}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors ${
                index === highlightedIndex
                  ? 'bg-primary-500/20'
                  : 'hover:bg-dark-700'
              }`}
            >
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 text-sm font-medium flex-shrink-0">
                {item.name?.charAt(0).toUpperCase() ||
                  item.email.charAt(0).toUpperCase()}
              </div>
              {/* User info */}
              <div className="min-w-0 flex-1">
                <p className="text-dark-100 text-sm truncate">{item.name}</p>
                <p className="text-dark-400 text-xs truncate">{item.email}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* No results message */}
      {isOpen && !isLoading && value.length >= minChars && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-dark-800 border border-dark-600 rounded-lg shadow-lg p-4">
          <p className="text-dark-400 text-sm text-center">No users found</p>
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
